import type { SendMessageDto } from '../types';

export interface LocalDraftMessage {
  id: string;
  subject: string;
  body: string;
  toRecipients: string[];
  ccRecipients?: string[];
  bccRecipients?: string[];
  transactionAmount: number;
  maxDeliveryFee: number;
  payFees: boolean;
  expirationDays: number;
  updatedAt: string;
}

const STORAGE_PREFIX = 'mailbox:drafts:v1';

function getStorageKey(username: string): string {
  return `${STORAGE_PREFIX}:${username.toLowerCase()}`;
}

function readAll(username: string): LocalDraftMessage[] {
  try {
    const raw = localStorage.getItem(getStorageKey(username));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as LocalDraftMessage[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(username: string, drafts: LocalDraftMessage[]): void {
  localStorage.setItem(getStorageKey(username), JSON.stringify(drafts));
}

export function listDrafts(username: string): LocalDraftMessage[] {
  return readAll(username).sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt));
}

export function getDraft(username: string, draftId: string): LocalDraftMessage | null {
  return readAll(username).find((draft) => draft.id === draftId) ?? null;
}

export function upsertDraft(username: string, payload: SendMessageDto, existingDraftId?: string): LocalDraftMessage {
  const drafts = readAll(username);
  const now = new Date().toISOString();
  const id = existingDraftId?.trim() || crypto.randomUUID();
  const next: LocalDraftMessage = {
    id,
    subject: payload.subject || '(No Subject)',
    body: payload.body || '',
    toRecipients: payload.toRecipients ?? [],
    ccRecipients: payload.ccRecipients ?? [],
    bccRecipients: payload.bccRecipients ?? [],
    transactionAmount: payload.transactionAmount ?? 0,
    maxDeliveryFee: payload.maxDeliveryFee ?? 0,
    payFees: payload.payFees ?? false,
    expirationDays: payload.expirationDays ?? 90,
    updatedAt: now,
  };

  const idx = drafts.findIndex((draft) => draft.id === id);
  if (idx >= 0) {
    drafts[idx] = next;
  } else {
    drafts.push(next);
  }

  writeAll(username, drafts);
  return next;
}

export function deleteDraft(username: string, draftId: string): void {
  const filtered = readAll(username).filter((draft) => draft.id !== draftId);
  writeAll(username, filtered);
}
